import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import ReactApexChart from 'react-apexcharts';
import { useEffect, useMemo, useState } from 'react';
import { Paper } from '@mui/material';
import moment from 'moment';
import apiService from '../../../core/services/api-client';
import Spinner from '../Spinner';

interface AlertEvolutionData {
  date: string;
  unique_skus_in_alert: number;
}

interface Props {
  companyId: string;
}

function AlertEvolutionWidget({ companyId }: Props) {
  const theme = useTheme();
  const [data, setData] = useState<AlertEvolutionData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    const api = apiService();
    api.get(`/metrics/${companyId}/alert-evolution`)
      .then((res) => {
        setData(res.data.data || []);
      })
      .catch(() => {
        setError('Error al cargar evolución de alertas');
      })
      .finally(() => setLoading(false));
  }, [companyId]);

  // Filtrar días con valor 0 (daemon no corrió) para línea continua
  const filteredData = useMemo(() => {
    return data.filter((d) => d.unique_skus_in_alert > 0);
  }, [data]);

  const labels = useMemo(() => {
    return filteredData.map((d) => moment(d.date).format('DD/MM/YYYY'));
  }, [filteredData]);

  const series = useMemo(() => {
    return [{
      name: 'SKUs en Alerta',
      data: filteredData.map((d) => d.unique_skus_in_alert),
    }];
  }, [filteredData]);

  const chartOptions: any = {
    chart: {
      fontFamily: 'inherit',
      foreColor: 'inherit',
      height: '100%',
      type: 'line',
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    colors: ['#1E88E5'],
    labels,
    dataLabels: { enabled: false },
    grid: {
      borderColor: theme.palette.divider,
    },
    legend: { show: false },
    stroke: {
      width: 3,
      curve: 'smooth',
    },
    tooltip: {
      followCursor: true,
      theme: theme.palette.mode,
    },
    xaxis: {
      axisBorder: { show: false },
      axisTicks: { color: theme.palette.divider },
      labels: {
        style: { colors: theme.palette.text.secondary, fontSize: '10px' },
        rotate: -45,
        rotateAlways: false,
        hideOverlappingLabels: true,
        showDuplicates: false,
        maxHeight: 80,
      },
      tickAmount: window.innerWidth < 768 ? 6 : Math.min(12, filteredData.length),
      tooltip: { enabled: false },
    },
    yaxis: {
      labels: {
        style: { colors: theme.palette.text.secondary },
        formatter: (value: number) => Math.round(value).toLocaleString(),
      },
      title: { text: 'SKUs en Alerta' },
    },
  };

  if (loading) {
    return (
      <Paper className="flex flex-col flex-auto shadow rounded-2xl overflow-hidden p-24 items-center justify-center" style={{ minHeight: 300 }}>
        <Spinner show={true} color="#000" />
        <span className="mt-8">Cargando evolución de alertas...</span>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper className="flex flex-col flex-auto shadow rounded-2xl overflow-hidden p-24 items-center justify-center" style={{ minHeight: 300 }}>
        <Typography color="error">{error}</Typography>
      </Paper>
    );
  }

  return (
    <Paper className="flex flex-col flex-auto shadow rounded-2xl overflow-hidden p-24">
      <Typography className="font-bold" color="text.secondary">
        Evolución de SKUs en Alerta (últimos 3 meses)
      </Typography>
      <div className="flex flex-col flex-auto" style={{ minHeight: 0 }}>
        <ReactApexChart
          className="flex-auto w-full"
          options={chartOptions}
          series={series}
          type="line"
          height={window.innerWidth < 768 ? 200 : 340}
        />
      </div>
    </Paper>
  );
}

export default AlertEvolutionWidget;